import { environment } from '@environments/environment';
import { collections } from '@shared/constants/collections';
import { constants } from '@shared/constants/constants';
import { CEMSTrigger, DataLayerCEMSBase } from '@shared/constants/gtm/gtm-datalayer-types.cems';
import { CIMSTrigger, DataLayerCIMSBase } from '@shared/constants/gtm/gtm-datalayer-types.cims';
import { DataLayerGFDBase, GFDTrigger } from '@shared/constants/gtm/gtm-datalayer-types.gfd';
import { DataLayerGPABase, GPATrigger } from '@shared/constants/gtm/gtm-datalayer-types.gpa';
import { DataLayerPERIBase, PERITrigger } from '@shared/constants/gtm/gtm-datalayer-types.peri';
import { ProfileStepCode } from '@shared/models/profile-step-code';
import { Profile } from '@webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@webapi/services/customer.service';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { getFromInjector } from './app-injector';
import { isPeri } from './extensions';

let gtmTagQueue: Promise<void> = Promise.resolve();

function pushGtmTag(profileStepCode: ProfileStepCode): void {
  const product = environment.orgId;
  const gtmService = getFromInjector(GoogleTagManagerService);
  const customerService = getFromInjector(CustomerService);
  const clientProfile = customerService.userData;

  void prepareGtmTag(product, profileStepCode, clientProfile)
    .then(gtmTag => {
      gtmTagQueue = gtmTagQueue
        .then(() => gtmService.pushTag(gtmTag))
        .then(() => {
          if (environment.envName !== 'prod') {
            console.log('gtm tag sent', { gtmTag });
          }
        })
        .catch(error => {
          console.error('error sending gtm tag', { gtmTag, error });
        });
    })
    .catch(error => {
      console.error('error preparing gtm tag', { profileStepCode, error });
    });
}

async function prepareGtmTag(product: string, profileStepCode: ProfileStepCode, clientProfile: Profile): Promise<any> {
  let gtmTag = {};

  switch (product) {
    case constants.orgId.cems.value: {
      gtmTag = prepareCemsGtmTag(profileStepCode, clientProfile);
      break;
    }
    case constants.orgId.cims.value: {
      gtmTag = prepareCimsGtmTag(profileStepCode, clientProfile);
      break;
    }
    case constants.orgId.peri.value: {
      gtmTag = preparePeriGtmTag(profileStepCode, clientProfile);
      break;
    }
    case constants.orgId.gpa.value: {
      gtmTag = prepareGpaGtmTag(profileStepCode, clientProfile);
      break;
    }
    case constants.orgId.gfd.value: {
      gtmTag = prepareGfdGtmTag(profileStepCode, clientProfile);
      break;
    }

    default: {
      gtmTag = prepareCemsGtmTag(profileStepCode, clientProfile);
    }
  }

  if (profileStepCode === ProfileStepCode.Finish) {
    gtmTag = await hashFinishContactFields(gtmTag);
  }

  return gtmTag;
}

function prepareCemsGtmTag(profileStepCode: ProfileStepCode, clientProfile: Profile) {
  let gtmTag = {
    produit: constants.orgId.cems.value.toUpperCase(),
    client: getYesNoFromBoolean(clientProfile.isMember),
    prospect_versement_libre_valeur: null,
    client_versement_libre_valeur: null,
    client_transfert_contrat: null,
    versement_programme: null,
    valeur_versement_programme: null,
    premier_versement_adhesion_valeur: null,
    type_gestion: null,
    gestion_sous_mandat_profil: null,
    type_clause_beneficiaire: null,
    gpa: null
  } as DataLayerCEMSBase;

  switch (profileStepCode) {
    case ProfileStepCode.MembershipCheck:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step1Home
      };
      break;
    case ProfileStepCode.ResidenceQuestions:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step2ResidenceQuestions
      };
      break;
    case ProfileStepCode.MyProfile:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step3PaymentProject
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyProfileSteps:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step4MyProfileStepsDdcIValidateMySaverProfile
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyProfileStepSummary:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step4MyProfileStepsDdc
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      gtmTag = extendTagBySelectedPlanData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContracts:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step5MyContractRappelDesVersements
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      gtmTag = extendTagBySelectedPlanData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBeneficiaries:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step6MyBeneficiariesType
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyDetails:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step7MyDetailsYourIdentity
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContacts:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step8MyDetailsYourContactDetails
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyFinancial:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step9MyDetailsYourProfessionalAndFinancialSituation
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBankDetails:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step10MyDetailsYourBankDetails
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Signature:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step11SignatureSummary
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.PhoneConfirmation:
      gtmTag = {
        ...gtmTag,
        event: CEMSTrigger.Step12ElectronicSignature
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Finish:
      gtmTag = {
        ...gtmTag,
        email: clientProfile.contact?.email || null,
        phone_number: clientProfile.contact?.mobilePhone || clientProfile.contact?.phone || null,
        event: CEMSTrigger.Step13SubscriptionValidationConfirmation
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    default:
      break;
  }

  return gtmTag;
}

function prepareCimsGtmTag(profileStepCode: ProfileStepCode, clientProfile: Profile) {
  let gtmTag = {
    produit: constants.orgId.cims.value.toUpperCase(),
    client: getYesNoFromBoolean(clientProfile.isMember),
    prospect_versement_libre_valeur: null,
    client_versement_libre_valeur: null,
    client_transfert_contrat: null,
    versement_programme: null,
    valeur_versement_programme: null,
    premier_versement_adhesion_valeur: null,
    type_gestion: null,
    gestion_sous_mandat_profil: null,
    type_clause_beneficiaire: null,
    gpa: null
  } as DataLayerCIMSBase;

  switch (profileStepCode) {
    case ProfileStepCode.MembershipCheck:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step1Home
      };
      break;
    case ProfileStepCode.ResidenceQuestions:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step2ResidenceQuestions
      };
      break;
    case ProfileStepCode.MyProfile:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step3PaymentProject
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyProfileSteps:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step4MyProfileStepsDdc
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyProfileStepSummary:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step4MyProfileStepsDdcIValidateMySaverProfile
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      gtmTag = extendTagBySelectedPlanData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContracts:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step5MyContractRappelDesVersements
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      gtmTag = extendTagBySelectedPlanData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyPremierRangMyBeneficiaries:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step6MyBeneficiariesPremierRangMyBeneficiaries
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBeneficiaries:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step7MyBeneficiariesType
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBeneficiariesContract:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step8MyBeneficiariesContractDate
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyDetails:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step9MyDetailsYourIdentity
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContacts:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step10MyDetailsYourContactDetails
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyFinancial:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step11MyDetailsYourProfessionalAndFinancialSituation
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBankDetails:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step12MyDetailsYourBankDetails
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Signature:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step13SignatureSummary
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.PhoneConfirmation:
      gtmTag = {
        ...gtmTag,
        event: CIMSTrigger.Step14ElectronicSignature
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Finish:
      gtmTag = {
        ...gtmTag,
        email: clientProfile.contact?.email || null,
        phone_number: clientProfile.contact?.mobilePhone || clientProfile.contact?.phone || null,
        event: CIMSTrigger.Step15SubscriptionValidationConfirmation
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    default:
      break;
  }

  return gtmTag;
}

function preparePeriGtmTag(profileStepCode: ProfileStepCode, clientProfile: Profile) {
  let gtmTag: DataLayerPERIBase = {
    produit: constants.orgId.peri.value.toUpperCase(),
    client: getYesNoFromBoolean(clientProfile.isMember),
    event: null,
    prospect_versement_libre_valeur: null,
    client_versement_libre_valeur: null,
    versement_programme: null,
    deduction_fiscale_versement: getHasTaxDeduction(clientProfile.retirementPlan?.hasTaxDeduction),
    type_gestion: null,
    gestion_sous_mandat_profil: null,
    type_clause_beneficiaire: null,
    valeur_versement_programme: null,
    email: null,
    phone_number: null
  };

  switch (profileStepCode) {
    case ProfileStepCode.MembershipCheck:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step1Home
      };
      break;
    case ProfileStepCode.ResidenceQuestions:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step2ResidenceQuestions
      };
      break;
    case ProfileStepCode.MyProfile:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step3PaymentProject
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyProfileSteps:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step4MyProfileStepsDdcIValidateMySaverProfile
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyProfileStepSummary:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step4MyProfileStepsDdc
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      gtmTag = extendTagBySelectedPlanData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContracts:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step5MyContractRappelDesVersements
      };
      gtmTag = extendTagByPaymentData(gtmTag, clientProfile);
      gtmTag = extendTagBySelectedPlanData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBeneficiaries:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step6MyBeneficiariesType
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyDetails:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step7MyDetailsYourIdentity
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContacts:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step8MyDetailsYourContactDetails
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyFinancial:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step9MyDetailsYourProfessionalAndFinancialSituation
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBankDetails:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step10MyDetailsYourBankDetails
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Signature:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step11SignatureSummary
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.PhoneConfirmation:
      gtmTag = {
        ...gtmTag,
        event: PERITrigger.Step12ElectronicSignature
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Finish:
      gtmTag = {
        ...gtmTag,
        email: clientProfile.contact?.email || null,
        phone_number: clientProfile.contact?.mobilePhone || clientProfile.contact?.phone || null,
        event: PERITrigger.Step13SubscriptionValidationConfirmation
      };
      gtmTag = extendTagByCemsMainPartData(gtmTag, clientProfile);
      gtmTag = extendTagByGpaProfileData(gtmTag, clientProfile);
      break;
    default:
      break;
  }

  return gtmTag;
}

function prepareGpaGtmTag(profileStepCode: ProfileStepCode, clientProfile: Profile) {
  let gtmTag: DataLayerGPABase = {
    produit: constants.orgId.gpa.value.toUpperCase(),
    client: getYesNoFromBoolean(clientProfile.isMember),
    event: null,
    garanti_x2_x3: null,
    montant_capital_garanti: null,
    type_clause_beneficiaire: null,
    email: null,
    phone_number: null
  };

  switch (profileStepCode) {
    case ProfileStepCode.MembershipCheck:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step1Home
      };
      break;
    case ProfileStepCode.ResidenceQuestions:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step2ResidenceQuestions
      };
      break;
    case ProfileStepCode.HealthQuestions:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step3MyProfileHealthQuestionnaire
      };
      break;
    case ProfileStepCode.MyRate:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step4MyContractChoiceOfGuaranteedCapital
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBeneficiaries:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step5MyBeneficiariesType
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyDetails:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step6MyDetailsYourIdentity
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContacts:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step7MyDetailsYourContactDetails
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyFinancial:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step8MyDetailsYourProfessionalAndFinancialSituation
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBankDetails:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step9MyDetailsYourBankDetails
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Signature:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step10SignatureSummary
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.PhoneConfirmation:
      gtmTag = {
        ...gtmTag,
        event: GPATrigger.Step11ElectronicSignature
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Finish:
      gtmTag = {
        ...gtmTag,
        email: clientProfile.contact?.email || null,
        phone_number: clientProfile.contact?.mobilePhone || clientProfile.contact?.phone || null,
        event: GPATrigger.Step12SubscriptionValidationConfirmation
      };
      gtmTag = extendByMyrateData(gtmTag, clientProfile);
      gtmTag = extendTagByBeneficiaryData(gtmTag, clientProfile);
      break;
    default:
      break;
  }

  return gtmTag;
}

function prepareGfdGtmTag(profileStepCode: ProfileStepCode, clientProfile: Profile) {
  let gtmTag: DataLayerGFDBase = {
    produit: constants.orgId.gfd.value.toUpperCase(),
    client: getYesNoFromBoolean(clientProfile.isMember),
    event: null,
    clause: null,
    montant_capital_garanti: null,
    mode_cotisation: null,
    email: null,
    phone_number: null
  };

  switch (profileStepCode) {
    case ProfileStepCode.MembershipCheck:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step1Home
      };
      break;
    case ProfileStepCode.ResidenceQuestions:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step2ResidenceQuestions
      };
      break;
    case ProfileStepCode.HealthQuestions:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step3MyProfileHealthQuestionnaire
      };
      break;
    case ProfileStepCode.MyRate:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step4MyContractChoiceOfGuaranteedCapital
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBeneficiaries:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step5MyBeneficiariesType
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyDetails:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step6MyDetailsYourIdentity
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyContacts:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step7MyDetailsYourContactDetails
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyFinancial:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step8MyDetailsYourProfessionalAndFinancialSituation
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.MyBankDetails:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step9MyDetailsYourBankDetails
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Signature:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step10SignatureSummary
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.PhoneConfirmation:
      gtmTag = {
        ...gtmTag,
        event: GFDTrigger.Step11ElectronicSignature
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    case ProfileStepCode.Finish:
      gtmTag = {
        ...gtmTag,
        email: clientProfile.contact?.email || null,
        phone_number: clientProfile.contact?.mobilePhone || clientProfile.contact?.phone || null,
        event: GFDTrigger.Step12SubscriptionValidationConfirmation
      };
      gtmTag = extendByMyGfdRateData(gtmTag, clientProfile);
      gtmTag = extendTagByGfdBeneficiaryData(gtmTag, clientProfile);
      break;
    default:
      break;
  }

  return gtmTag;
}

function getYesNoFromBoolean(value: boolean | null | undefined): 'non' | 'oui' | null {
  if (value === true) {
    return gtmYesAnser;
  } else if (value === false) {
    return gtmNoAnser;
  } else {
    return null;
  }
}
function getHasTaxDeduction(value: boolean | null | undefined): 'non' | 'oui' | null {
  if (value === true) {
    return gtmNoAnser;
  } else if (value === false) {
    return gtmYesAnser;
  } else {
    return null;
  }
}

function getTypeGestion(clientProfile: Profile): 'libre' | 'sous_mandat' | 'horizon' | null {
  if (clientProfile.managementTypeCode === constants.gestionLibre.id) {
    return gtmLibre;
  } else if (clientProfile.managementTypeCode === constants.gestionSousMandat.id) {
    return gtmSousMandat;
  } else if (clientProfile.managementTypeCode === constants.gestionHorizon.id) {
    return gtmHorizon;
  } else {
    return null;
  }
}

function getGestionSousMandatProfil(clientProfile: Profile): string | null {
  switch (clientProfile.selectedInvestmentProfileId) {
    case constants.prudent.id:
      return constants.prudent.uiName;
    case constants.modere.id:
      // todo: delete when temporary override will be removed MIFS-704 CR in the comments
      if (isPeri()) {
        return constants.equilibré.uiName;
      } else {
        return constants.modere.uiName;
      }
    case constants.equilibré.id:
      return constants.equilibré.uiName;
    case constants.dynamiqueA.id:
      return constants.dynamiqueA.uiName;
    case constants.dynamiqueR.id:
      return constants.dynamiqueR.uiName;
    case constants.offensif.id:
      return constants.offensif.uiName;
    default:
      return null;
  }
}

function extendTagByCemsMainPartData(gtmTag: any, clientProfile: Profile) {
  let tag = extendTagByPaymentData(gtmTag, clientProfile);
  tag = extendTagBySelectedPlanData(tag, clientProfile);
  tag = extendTagByBeneficiaryData(tag, clientProfile);
  return tag;
}

function extendTagBySelectedPlanData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    type_gestion: getTypeGestion(clientProfile),
    gestion_sous_mandat_profil: getGestionSousMandatProfil(clientProfile)
  };
}

function extendByMyrateData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    garanti_x2_x3: clientProfile.gpaProfile?.isMultipliedCoverage ? 'oui' : 'non',
    montant_capital_garanti: clientProfile.gpaProfile?.guaranteedCapitalCode || null
  };
}
function extendByMyGfdRateData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    mode_cotisation:
      collections.gfdLifetimeTypes.find(x => x.periodicitePaiement === clientProfile.gfdProfile?.contributionTypeCode)
        ?.gtmTagName || null,
    montant_capital_garanti: clientProfile.gfdProfile?.guaranteedCapitalCode || null
  };
}

function extendTagByBeneficiaryData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    type_clause_beneficiaire: getTypeClauseBeneficiaire(clientProfile)
  };
}
function extendTagByGfdBeneficiaryData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    clause:
      clientProfile.gfdProfile?.beneficiaryTypeCode === constants.beneficiaresTypesGfd.funeralOperator.code
        ? 'operateur_funeraire'
        : 'personne_physique'
  };
}

function extendTagByPaymentData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    prospect_versement_libre_valeur: clientProfile.isMember ? null : clientProfile.initialPaymentAmount,
    client_versement_libre_valeur: clientProfile.isMember ? clientProfile.initialPaymentAmount : null,
    client_transfert_contrat: getYesNoFromBoolean(clientProfile.doTransferContract),
    premier_versement_adhesion_valeur: clientProfile.transferContractAmount || null, // to be checked
    versement_programme: getYesNoFromBoolean(!!clientProfile.scheduledPaymentAmount),
    valeur_versement_programme: clientProfile.scheduledPaymentAmount || null
  };
}

function extendTagByGpaProfileData(gtmTag: any, clientProfile: Profile) {
  return {
    ...gtmTag,
    produit: !clientProfile.gpaProfile
      ? gtmTag.produit
      : `${gtmTag.produit}+${constants.orgId.gpa.value.toUpperCase()}`,
    gpa: getYesNoFromBoolean(!!clientProfile.gpaProfile)
  };
}

function getTypeClauseBeneficiaire(clientProfile: Profile) {
  switch (clientProfile.beneficiaryClauseCode) {
    case constants.beneficiaresTypes.standard.code:
      return 'standard';
    case constants.beneficiaresTypes.nominative.code:
      return 'nominative';
    case constants.beneficiaresTypes.libre.code:
      return 'libre';
    default:
      return null;
  }
}

async function hashFinishContactFields(gtmTag: any): Promise<any> {
  return {
    ...gtmTag,
    email: await hashGtmValue(gtmTag.email),
    phone_number: await hashGtmValue(gtmTag.phone_number)
  };
}

async function hashGtmValue(value: string | null | undefined): Promise<string | null> {
  const normalizedValue = value?.trim();

  if (!normalizedValue) {
    return null;
  }

  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalizedValue));

  return Array.from(new Uint8Array(hashBuffer))
    .map(item => item.toString(16).padStart(2, '0'))
    .join('');
}

export { pushGtmTag };

export const gtmYesAnser = 'oui';
export const gtmNoAnser = 'non';
export const gtmLibre = 'libre';
export const gtmSousMandat = 'sous_mandat';
export const gtmHorizon = 'horizon';
